import React, { useState, useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import DrEditor from "./Editor";
import SectionWithAdd from "./SectionWithAdd";
import SequenceableControls from "./SequenceableControls";
import Spinner from "../../Spinner";
import StatefulRichTextList from "./StatefulRichTextList";

/**
 * <StatefulModules syllabusId={} api={} editMode={} reqOptions={} focus={} focusKey={} lastUpdateType={} defaultText={} containerId={} canViewProgress={} canEditProgress={} progressA={} progAIndex={} toggleProgressA={} childTypes={}  />
 */
const StatefulModules = (props) => {
  try {
    const _isMounted = useRef(false);
    const {
      syllabusId,
      progAIndex,
      api,
      editMode,
      reqOptions,
      lastUpdateType,
      updateTime,
      sectionName,
    } = props;
    const url = `${api}units`;
    const [items, setItems] = useState(props.units || []);
    const [maxSequence, setMaxSequence] = useState(items.length);
    const [dataReady, setDataReady] = useState(true);
    const [reload, setReload] = useState(false);

    useEffect(function updateMounted() {
      _isMounted.current = true;
      return function cleanupMounted() {
        _isMounted.current = false;
      };
    }, []);

    useEffect(
      function modPropUpdate() {
        if (props.units) {
          setItems(props.units);
          setMaxSequence(props.units.length);
        }
      },
      [props.units]
    );

    useEffect(
      function fetchItems() {
        if (reload) {
          apiCall(
            `${url}?unit[syllabus_id]=${syllabusId}`,
            reqOptions("GET"),
            (res) => {
              res.json().then((data) => {
                if (_isMounted.current) {
                  setItems(data);
                  setMaxSequence(data.length);
                  setReload(false);
                }
              });
            }
          );
        }
      },
      [reload]
    );

    useEffect(
      function reloadEffect() {
        if (lastUpdateType === "unit" && _isMounted.current) {
          setReload(true);
        }
      },
      [updateTime]
    );

    const addItem = (opts, e) => {
      const body = {};
      body["unit"] = {
        syllabus_id: syllabusId,
        sequence: opts["sequence"],
      };
      apiCall(url, reqOptions("POST", body), (res) => setReload(true));
    };

    const removeItem = (opts, e) => {
      if (confirm("Are you sure you want to remove this unit/module?")) {
        const { id } = opts;
        const body = {};
        body["unit"] = { syllabus_id: syllabusId, active: false };
        apiCall(`${url}/${id}`, reqOptions("PATCH", body), (res) =>
          setReload(true)
        );
      }
    };

    const updateItemSequence = (opts, e) => {
      const { id, value } = opts;
      const body = {};
      body["unit"] = { syllabus_id: syllabusId, sequence: value };
      apiCall(`${url}/${id}`, reqOptions("PATCH", body), (res) =>
        setReload(true)
      );
    };

    const display =
      items.length > 0 || editMode ? (
        <SectionWithAdd
          {...props}
          id={`unit-section`}
          type="unit"
          plType="units"
          showProgress={props.canViewProgress}
          progIndex={progAIndex}
          addItem={addItem}
          sectionName={sectionName}
        >
          <ul className="pl-0 top-buffer sm-font">
            {items.map((i, ix) => (
              <Module
                {...props}
                key={`unit-${i.id}`}
                parentKey={`unit-${i.id}`}
                item={i}
                ix={ix}
                maxSequence={maxSequence}
                handleChange={updateItemSequence}
                removeItem={removeItem}
                dataReady={dataReady}
              />
            ))}
          </ul>
        </SectionWithAdd>
      ) : (
        ""
      );

    return dataReady ? display : <Spinner />;
  } catch (e) {
    console.error(e);
  }
};

/**
 * Stateful single unit/module
 */
const Module = (props) => {
  try {
    const {
      api,
      containerId,
      dataReady,
      editMode,
      focusKey,
      item,
      ix,
      childTypes,
      parentKey,
      reqOptions,
      syllabusId,
    } = props;
    const url = `${api}units/${item.id}`;
    const [description, setDescription] = useState(item.assmt_desc || "");
    const [name, setName] = useState(item.name || "");
    const [timer, setTimer] = useState(null);
    const _isMounted = useRef(false);
    const _localUpdate = useRef(false);
    const inFocus = focusKey == parentKey;

    const updateName = (e) => {
      if (name != e.target.value) {
        _localUpdate.current = true;
        setName(e.target.value);
      }
    };

    const updateDescription = (opts, e) => {
      if (description != e.target.value) {
        _localUpdate.current = true;
        setDescription(e.target.value);
      }
    };

    const debounce = (fn) => {
      if (timer) clearTimeout(timer);
      if (_isMounted.current) {
        setTimer(
          setTimeout(() => {
            fn();
          }, 300)
        );
      }
    };

    useEffect(() => {
      _isMounted.current = true;
      return () => {
        console.log("cleaup");
        _isMounted.current = false;
      };
    }, []);

    useEffect(() => {
      if (_localUpdate.current) {
        const body = { container_id: containerId };
        body["unit"] = {
          syllabus_id: syllabusId,
          assmt_desc: description,
          name: name,
        };
        debounce(() => {
          apiCall(url, reqOptions("PATCH", body));
        });
      }
    }, [description, name, props.item.assmt_desc, props.item.name]);

    useEffect(() => {
      _localUpdate.current = false;
      setDescription(props.item.assmt_desc);
    }, [props.item.assmt_desc]);

    useEffect(() => {
      _localUpdate.current = false;
      setName(props.item.name);
    }, [props.item.name]);

    const moduleName = inFocus ? (
      <>
        <label htmlFor={`${parentKey}nameinput`}>
          <strong>Unit/Module Name:</strong>
        </label>
        <textarea
          id={`${parentKey}nameinput`}
          className="col-11"
          placeholder="Module/unit name..."
          onChange={(e) => updateName(e)}
          value={name}
        />
      </>
    ) : (
      <div>
        <strong>{name}</strong>
        <span
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
        />
      </div>
    );
    const descEditor = inFocus ? (
      <>
        <label htmlFor={`${parentKey}-desc-input`}>
          <strong>Unit/Module Description:</strong>
        </label>
        <DrEditor
          wrapperId={`${parentKey}-desc-input`}
          updateType="unit"
          defaultText={`Module/Unit ${item.sequence} description...`}
          text={description || ""}
          parentKey={parentKey}
          handleChange={updateDescription}
          changeOpts={{}}
          focusKey={focusKey}
          dataReady={dataReady}
        />
      </>
    ) : (
      ""
    );
    // TODO: should rename Topic the correct way!
    const childLists = childTypes.map((t) => {
      if (t.name == "Topic") {
        t.name = "Daily Learning Objectives/Topic";
      }
      return editMode || t.show ? (
        <StatefulRichTextList
          {...props}
          key={`unit-${item.id}-descs-type-${t.id}`}
          items={item.descendants_by_type[t.id]}
          listTitle={`${t.name}(s)`}
          unitId={item.id}
          typeLookupId={t.id}
          type="unit_descendant"
          plType="unit_descendants"
        />
      ) : null;
    });

    return (
      <li id={parentKey} className="test-unit list-unstyled">
        <div className="d-flex justify-content-between align-items-top">
          <div
            className="d-flex justify-content-between w-75 align-items-top"
            onClick={(e) => props.focus(parentKey, e)}
          >
            <span className="sm-font">
              <strong>{item.sequence}.</strong>
            </span>
            <span className="col-12">
              {moduleName}
              {descEditor}
            </span>
          </div>
          <SequenceableControls
            {...props}
            itemIndex={ix}
            type="unit"
            plType="units"
          />
        </div>
        <div className="pl-5 pr-5">{childLists}</div>
      </li>
    );
  } catch (e) {
    console.error(e);
  }
};

const apiCall = (url, reqOptions, next) => {
  try {
    fetch(url, reqOptions).then((res) => {
      if (res.status === 401) {
        // alert('Unauthorized. Either your session has expired or you are not authorized to perform this action. Please try logging out and logging in again.')
        // if (confirm('Unauthorized. Either your session has expired or you are not authorized to perform this action. Redirect to login page? \n(Please note: if your session has expired, none of the changes you make to this syllabus will be saved until you log in again.)')) {
        //   sylContainer.props.history.push('/log_in');
        // }
        alert(
          "Not authorized to update the syllabus. Please reload the page and try again."
        );
      } else if (res.status >= 400) {
        alert(
          "There was an issue updating the syllabus. Please reload the page and try again."
        );
      } else if (next) {
        next(res);
      }
    });
  } catch (err) {
    //To Do: handle error
  }
};

export default StatefulModules;
